import { BaseDialog } from '@/components/BaseDialog'
import { SectionCard } from '@/components/SectionCard'
import { DepartmentItemsSkeleton } from '@/components/skeletons/DepartmentItemsSkeleton'
import { Badge } from '@/components/ui/badge'
import { useGetDepartmentItems } from '@/features/departments/hooks/useDepartment'
import { ItemStats } from '@/features/items/components/ItemStats'
import { DepartmentPreview } from '@/features/map/components/DepartmentPreview'

import { Dot, Shape } from '@/features/map/types/shape'
import { useTranslation } from 'react-i18next'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  departmentId: string
  departmentShape: Shape
  gatewayDots?: Dot[]
  deviceDots?: Dot[]
  isLoading?: boolean
}

export function DepartmentTrackingDialog({
  open,
  onOpenChange,
  departmentId,
  departmentShape,
  gatewayDots = [],
  deviceDots = [],
  isLoading,
}: Props) {
  const { t } = useTranslation()
  const { data: departmentItems, isFetching: isFetchingDepartment } = useGetDepartmentItems(
    departmentId,
    undefined,
    !!departmentId && open,
  )

  const department = departmentItems?.department
  const items = departmentItems?.items

  const bleGateways = gatewayDots.filter(g => g.gatewayType === 'BLE')
  const rfidGateways = gatewayDots.filter(g => g.gatewayType === 'RFID')

  const mappedDeviceDots = deviceDots.map(dot => {
    const item = items?.find(i => i.tagId === dot.id)

    return {
      ...dot,
      label: item?.productName ?? dot.label,
      item,
    }
  })

  return (
    <BaseDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('department.departmentTracking.title')}
      isLoading={isLoading}
      dialogContentClass="sm:max-w-6xl"
      contentTestId="department-tracking-dialog"
    >
      <div className="grid h-full grid-cols-12">
        {/* LEFT MAP */}
        <div className="col-span-12 overflow-auto p-4 md:col-span-8 md:border-r">
          <div className="flex justify-center">
            <DepartmentPreview
              departmentShape={departmentShape}
              gatewayDots={gatewayDots}
              deviceDots={mappedDeviceDots}
            ></DepartmentPreview>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="col-span-12 md:col-span-4 overflow-y-auto p-4">
          <div className="flex flex-col gap-4">
            {/* BLE */}
            <SectionCard title={t('department.departmentTracking.bleTitle')} headerClass="pb-0">
              {bleGateways.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('department.departmentTracking.noBle')}</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {bleGateways.map(gateway => (
                    <div
                      key={gateway.id}
                      className="flex items-center justify-between rounded-md border bg-secondary/30 p-3"
                    >
                      <div className="flex flex-col">
                        <span data-testid="ble-name" className="text-sm font-medium">
                          {gateway.label}
                        </span>
                      </div>

                      <Badge>BLE</Badge>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>

            {/* RFID */}
            <SectionCard title={t('department.departmentTracking.rfidTitle')} headerClass="pb-0">
              {rfidGateways.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('department.departmentTracking.noRfid')}</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {rfidGateways.map(gateway => (
                    <div
                      key={gateway.id}
                      className="flex items-center justify-between rounded-md border bg-secondary/30 p-3"
                    >
                      <div className="flex flex-col">
                        <span data-testid="rfid-name" className="text-sm font-medium">
                          {gateway.label}
                        </span>
                      </div>

                      <Badge variant="destructive">RFID</Badge>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>

            {/* DEVICES */}

            {isFetchingDepartment ? (
              <DepartmentItemsSkeleton />
            ) : (
              <SectionCard title={t('common.equipment')} headerClass="pb-0">
                {items?.length === 0 ? (
                  <p className="mt-2 text-xs text-muted-foreground">{t('department.noEquipment')}</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {items?.map(item => {
                      return (
                        <ItemStats
                          key={item.id}
                          title={item.productName}
                          subtitle={item.serialNumber}
                          status={item.status}
                          days={item.daysSinceLastStatusChange}
                        ></ItemStats>
                      )
                    })}
                  </div>
                )}
              </SectionCard>
            )}
          </div>
        </div>
      </div>
    </BaseDialog>
  )
}
